import React from 'react';
import { Icon, SvgIcon } from '@mui/material';
import * as MaterialIcons from '@mui/icons-material';

interface IconRendererProps {
  icon: string;
  iconType?: 'material' | 'service' | 'custom';
  iconUrl?: string;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  sx?: any;
}

const IconRenderer: React.FC<IconRendererProps> = ({
  icon,
  iconType = 'material',
  iconUrl,
  size = 'medium',
  color,
  sx = {}
}) => {
  const getSizeProps = () => {
    switch (size) {
      case 'small':
        return { fontSize: 'small' as const, sx: { fontSize: '1rem', ...sx } };
      case 'large':
        return { fontSize: 'large' as const, sx: { fontSize: '2rem', ...sx } };
      default:
        return { fontSize: 'medium' as const, sx: { fontSize: '1.5rem', ...sx } };
    }
  };

  // Handle custom URL icons
  if (iconType === 'custom' && iconUrl) {
    return (
      <img
        src={iconUrl}
        alt={icon}
        style={{
          width: size === 'small' ? 16 : size === 'large' ? 32 : 24,
          height: size === 'small' ? 16 : size === 'large' ? 32 : 24,
          objectFit: 'contain',
          ...sx
        }}
      />
    );
  }

  // Handle service icons (could be expanded with more services)
  if (iconType === 'service') {
    // For now, fall back to material icons
    // Could be extended to include service-specific icons
    return (
       <img
        src={iconUrl}
        alt={icon}
        style={{
          width: size === 'small' ? 16 : size === 'large' ? 32 : 24,
          height: size === 'small' ? 16 : size === 'large' ? 32 : 24,
          objectFit: 'contain',
          ...sx
        }}
      />
    )
  }

  // Handle Material Icons
  try {
    // Convert icon name to PascalCase for Material Icons
    const iconName = icon
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
    
    // Get the Material UI icon component
    const MaterialIcon = (MaterialIcons as any)[iconName];
    
    if (MaterialIcon) {
      const sizeProps = getSizeProps();
      return (
        <MaterialIcon 
          {...sizeProps}
          sx={{ 
            ...sizeProps.sx,
            color: color || 'inherit'
          }}
        />
      );
    }
  } catch (error) {
    console.warn(`Icon "${icon}" not found in Material Icons`);
  }

  // Fallback to generic Material UI Icon with text
  const sizeProps = getSizeProps();
  return (
    <Icon 
      {...sizeProps}
      sx={{
        ...sizeProps.sx,
        color: color || 'inherit',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {icon.charAt(0).toUpperCase()}
    </Icon>
  );
};

export { IconRenderer };